/**
 * The Closing Email source list (which source, its name filter and its page)
 * held in the URL, so a link or a refresh lands on the same list:
 * `/closing/email?source=tag&q=austin&page=2`.
 *
 * Same rules as `features/company-lists/useCompanyListsFilters.ts`:
 * - every write is `{ replace: true }`: a filter is a view of the page, not a
 *   step Back should walk through;
 * - a filter change drops `page` in the same write, and a source change drops
 *   both `q` and `page`, so no render pairs a new filter with an old page;
 * - `page` is read leniently (garbage or < 1 is 1) and page 1 is no param;
 *   a page past the end is clamped by the list that shows it;
 * - `q` is trimmed both ways, so a shared link never filters on a space;
 * - the default source (`search`) is no param; an unknown one reads as it and
 *   is dropped on the next write.
 *
 * Only the browsing state lives here. The chosen search, network or tag, the
 * selected recipients and the email stay in component state: they start a
 * send, and a shared link must never pre-select who gets an email.
 */
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type ClosingSource = "search" | "network" | "tag";

const SOURCE_PARAM = "source";
const Q_PARAM = "q";
const PAGE_PARAM = "page";
const SOURCES: readonly ClosingSource[] = ["search", "network", "tag"];

function readSource(params: URLSearchParams): ClosingSource {
  const raw = params.get(SOURCE_PARAM) as ClosingSource | null;
  return raw && SOURCES.includes(raw) ? raw : "search";
}

/** Same parse as People Lists' / Company Lists' `readPage`. */
function readPage(params: URLSearchParams): number {
  const parsed = Number.parseInt(params.get(PAGE_PARAM) ?? "1", 10);
  if (!Number.isSafeInteger(parsed)) return 1;
  return Math.max(1, parsed);
}

export interface ClosingSourceUrl {
  source: ClosingSource;
  /** The active source's name filter; "" = none. */
  q: string;
  page: number;
  setSource: (next: ClosingSource) => void;
  setQ: (next: string) => void;
  setPage: (next: number) => void;
}

export function useClosingSourceUrl(): ClosingSourceUrl {
  const [params, setParams] = useSearchParams();
  const source = readSource(params);
  const q = params.get(Q_PARAM)?.trim() ?? "";
  const page = readPage(params);

  const write = useCallback(
    (edit: (next: URLSearchParams) => void) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          // An unknown `source` reads as search; do not carry it forward.
          if (next.has(SOURCE_PARAM) && readSource(next) === "search") next.delete(SOURCE_PARAM);
          edit(next);
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );

  const setSource = useCallback(
    (nextSource: ClosingSource) =>
      write((next) => {
        if (nextSource === "search") next.delete(SOURCE_PARAM);
        else next.set(SOURCE_PARAM, nextSource);
        next.delete(Q_PARAM);
        next.delete(PAGE_PARAM);
      }),
    [write]
  );

  const setQ = useCallback(
    (text: string) =>
      write((next) => {
        const nextQ = text.trim();
        if (nextQ) next.set(Q_PARAM, nextQ);
        else next.delete(Q_PARAM);
        next.delete(PAGE_PARAM);
      }),
    [write]
  );

  const setPage = useCallback(
    (nextPage: number) =>
      write((next) => {
        if (nextPage > 1) next.set(PAGE_PARAM, String(nextPage));
        else next.delete(PAGE_PARAM);
      }),
    [write]
  );

  return useMemo(
    () => ({ source, q, page, setSource, setQ, setPage }),
    [source, q, page, setSource, setQ, setPage]
  );
}
